/**
 * Investor dashboard — one screen, everything an investor needs at a glance.
 *
 * Six panels stacked vertically:
 *   1. Your shareholdings — Class A + Class B + total + ownership %
 *   2. Company KPIs — publishers, installs, sessions (+ 24h delta)
 *   3. Network stats — reach + financials, when available
 *   4. Announcements — pinned + recent
 *   5. Your transaction history — share grants / changes
 *   6. Documents — shared files (legal, reports, etc.)
 *
 * No filtering, no admin actions, no edits. Read-only by design — the
 * investor experience is "see what you own and where the company is
 * going," not "manage shareholders."
 */
import {
  useCompanyMetrics,
  useMyAnnouncements,
  useMyDocuments,
  useMyShares,
  useMyTransactions,
  useNetworkStats
} from '@/hooks/usePortal';
import {
  Card,
  Pagination as _Pagination,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import {
  fmtDate,
  fmtDateTime,
  fmtNum
} from '@/lib/format';
import { ExternalLink, Pin } from 'lucide-react';

const pct = (n?: number | null) =>
  n != null && Number.isFinite(n) ? `${Number(n).toFixed(2)}%` : '—';

const CATEGORY_BADGE: Record<string, string> = {
  milestone: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  product: 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30',
  fundraising: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  team: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  general: 'bg-white/10 text-nurev-muted ring-1 ring-white/10'
};

export function DashboardScreen() {
  const { data: shares, isLoading: sharesLoading } = useMyShares();
  const { data: metrics, isLoading: metricsLoading } = useCompanyMetrics();
  const { data: network } = useNetworkStats();
  const { data: announcements, isLoading: annLoading } = useMyAnnouncements(10);
  const { data: transactions, isLoading: txLoading } = useMyTransactions(25);
  const { data: documents, isLoading: docsLoading } = useMyDocuments();

  return (
    <div className="space-y-8">
      {/* ─── Holdings ─── */}
      <section>
        <h1 className="text-2xl font-bold text-nurev-text">Your shareholdings</h1>
        <p className="mt-1 text-sm text-nurev-muted">
          Current position in NUREV.IO, Inc.
        </p>

        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            label="Class A (Voting)"
            value={fmtNum(shares?.classAShares)}
            sub="Board-class voting shares"
            loading={sharesLoading}
          />
          <StatCard
            label="Class B (Non-Voting)"
            value={fmtNum(shares?.classBShares)}
            sub="Equity, no voting rights"
            loading={sharesLoading}
          />
          <StatCard
            label="Total Shares"
            value={fmtNum(shares?.totalShares)}
            sub={
              shares
                ? `of ${fmtNum(shares.classATotalShares + shares.classBTotalShares)} total outstanding`
                : undefined
            }
            color="text-emerald-300"
            loading={sharesLoading}
          />
          <StatCard
            label="Ownership"
            value={pct(shares?.fullyDilutedPercent)}
            sub="Fully-diluted"
            color="text-emerald-300"
            loading={sharesLoading}
          />
        </div>
      </section>

      {/* ─── Company KPIs ─── */}
      <section>
        <SectionLabel>Company snapshot</SectionLabel>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <StatCard
            label="Total Publishers"
            value={fmtNum(metrics?.totalPublishers)}
            sub={
              metrics?.monthlyGrowth?.publishers != null
                ? `${metrics.monthlyGrowth.publishers >= 0 ? '+' : ''}${fmtNum(metrics.monthlyGrowth.publishers)} this month`
                : undefined
            }
            loading={metricsLoading}
          />
          <StatCard
            label="Lifetime Installs"
            value={fmtNum(metrics?.installs)}
            sub={
              metrics?.monthlyGrowth?.installs != null
                ? `+${fmtNum(metrics.monthlyGrowth.installs)} this month`
                : undefined
            }
            loading={metricsLoading}
          />
          <StatCard
            label="Sessions (Lifetime)"
            value={fmtNum(metrics?.sessions)}
            sub={
              metrics?.monthlyGrowth?.sessions != null
                ? `+${fmtNum(metrics.monthlyGrowth.sessions)} this month`
                : undefined
            }
            loading={metricsLoading}
          />
        </div>
      </section>

      {/* ─── Network stats (optional, only render if available) ─── */}
      {network && (network.reach || network.financials) && (
        <section>
          <SectionLabel>Network reach &amp; financials</SectionLabel>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {network.reach?.countries != null && (
              <StatCard
                label="Countries"
                value={fmtNum(network.reach.countries)}
              />
            )}
            {network.reach?.devices != null && (
              <StatCard
                label="Devices on Network"
                value={fmtNum(network.reach.devices)}
              />
            )}
            {network.financials?.totalPaidOut != null && (
              <StatCard
                label="Lifetime Paid Out"
                value={`$${fmtNum(network.financials.totalPaidOut)}`}
                color="text-emerald-300"
              />
            )}
            {network.financials?.activePayouts != null && (
              <StatCard
                label="Active Payouts"
                value={fmtNum(network.financials.activePayouts)}
              />
            )}
          </div>
        </section>
      )}

      {/* ─── Announcements ─── */}
      <section>
        <SectionLabel>Announcements</SectionLabel>
        <Card>
          {annLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !announcements?.docs.length ? (
            <p className="py-6 text-center text-sm text-nurev-muted">
              No announcements yet. Important company updates land here.
            </p>
          ) : (
            <div className="space-y-3">
              {announcements.docs.map(a => (
                <div
                  key={a._id}
                  className="rounded-lg border border-nurev-border bg-white/[0.02] p-4"
                >
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {a.pinned && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium text-amber-300 ring-1 ring-amber-500/30">
                        <Pin className="h-3 w-3" /> Pinned
                      </span>
                    )}
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${CATEGORY_BADGE[a.category] || CATEGORY_BADGE.general}`}
                    >
                      {a.category}
                    </span>
                    <p className="text-[10px] text-nurev-muted">
                      {fmtDate(a.publishedAt)}
                    </p>
                  </div>
                  <h3 className="mb-1 text-sm font-semibold text-nurev-text">
                    {a.title}
                  </h3>
                  <p className="whitespace-pre-wrap text-xs leading-relaxed text-nurev-text-soft">
                    {a.body}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* ─── Transactions ─── */}
      <section>
        <SectionLabel>Your share transactions</SectionLabel>
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Class</th>
                  <th className="px-4 py-3 text-right font-medium">Change</th>
                  <th className="px-4 py-3 text-right font-medium">New Total</th>
                  <th className="px-4 py-3 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {txLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i} className="border-b border-nurev-border/50">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : !transactions?.docs.length ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-nurev-muted"
                    >
                      No transactions yet.
                    </td>
                  </tr>
                ) : (
                  transactions.docs.map(t => (
                    <tr key={t._id} className="border-b border-nurev-border/50">
                      <td className="px-4 py-2 text-xs text-nurev-muted">
                        {fmtDate(t.createdAt)}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${
                            t.shareClass === 'A'
                              ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30'
                              : 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30'
                          }`}
                        >
                          Class {t.shareClass}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-2 text-right font-mono text-xs ${
                          t.change >= 0 ? 'text-emerald-300' : 'text-red-300'
                        }`}
                      >
                        {t.change >= 0 ? '+' : ''}
                        {fmtNum(t.change)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-nurev-text">
                        {fmtNum(t.newShares)}
                      </td>
                      <td className="px-4 py-2 text-xs text-nurev-muted">
                        {t.reason || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* ─── Documents ─── */}
      <section>
        <SectionLabel>Shared documents</SectionLabel>
        <Card>
          {docsLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : !documents?.docs.length ? (
            <p className="py-6 text-center text-sm text-nurev-muted">
              No documents shared yet.
            </p>
          ) : (
            <div className="space-y-2">
              {documents.docs.map(d => (
                <a
                  key={d._id}
                  href={d.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between rounded-lg bg-white/[0.03] p-3 transition hover:bg-white/[0.06]"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-nurev-text">
                      {d.title}
                    </p>
                    <p className="text-[10px] text-nurev-muted">
                      {d.fileName} · {fmtDateTime(d.createdAt)}
                    </p>
                    {d.description && (
                      <p className="mt-0.5 truncate text-[10px] text-nurev-muted">
                        {d.description}
                      </p>
                    )}
                  </div>
                  <ExternalLink className="h-4 w-4 shrink-0 text-emerald-300" />
                </a>
              ))}
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
